import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Modal,
  Tooltip,
  Select,
  InputNumber,
  Segmented,
  Alert,
  message
} from 'antd';
import { ReloadOutlined, ThunderboltOutlined, SendOutlined, TagsOutlined } from '@ant-design/icons';
import api from '../api';
import { useI18n } from '../i18n';

// 标签展示配色：按标签名散列到固定颜色，同名标签在任何位置颜色一致
const TAG_COLORS = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'geekblue', 'purple'];
const colorOfTag = (tag) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) % TAG_COLORS.length;
  return TAG_COLORS[hash];
};

// 与 backend/config/constants.js 保持一致：低于此值连转账 gas 都不够
const MIN_BNB_FOR_TOPUP = 0.0001;

// 钱包列表：标签（颜色区分私钥导入状态）、地址、链上实时余额、操作
const WalletList = ({ wallets, loading, onRefresh, onViewTransactions, addressBook = [] }) => {
  const { t, ts } = useI18n();
  const [distributing, setDistributing] = useState(false);
  // 转账弹窗状态
  const [sendFrom, setSendFrom] = useState(null); // 发送方钱包对象，非空时弹窗打开
  const [sendToken, setSendToken] = useState('USDT');
  const [sendTo, setSendTo] = useState(undefined);
  const [sendAmount, setSendAmount] = useState(null);
  const [sending, setSending] = useState(false);
  // 标签编辑弹窗状态
  const [tagWallet, setTagWallet] = useState(null); // 编辑中的钱包，非空时弹窗打开
  const [tagValues, setTagValues] = useState([]);
  const [tagOptions, setTagOptions] = useState([]); // 已有标签（下拉候选）
  const [savingTags, setSavingTags] = useState(false);

  // 打开标签弹窗：带出当前标签，并拉取已有标签供下拉选择
  const openTags = async (w) => {
    setTagWallet(w);
    setTagValues(w.tags || []);
    try {
      const res = await api.get('/wallets/tags');
      setTagOptions(res.data.data.map((t) => ({ value: t.tag, label: `${t.tag}（${t.count}）` })));
    } catch (err) {
      // 候选拉取失败不影响手动输入新标签
      setTagOptions([]);
    }
  };

  const handleSaveTags = async () => {
    if (!tagWallet) return;
    setSavingTags(true);
    try {
      await api.put(`/wallets/${tagWallet.address}/tags`, { tags: tagValues });
      message.success(t('已保存 {label} 的标签', { label: tagWallet.label }));
      setTagWallet(null);
      onRefresh();
    } catch (err) {
      message.error(t('保存失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSavingTags(false);
    }
  };

  // gas 分发：从 BNB 最多的钱包给不足的钱包各转种子 BNB
  const handleDistribute = () => {
    Modal.confirm({
      title: t('分发 gas'),
      content: t('将从 BNB 最多的钱包，给 BNB 不足 0.0005 的其他钱包各转 0.0005 BNB（逐笔链上转账）。确认执行？'),
      okText: t('确认分发'),
      cancelText: t('取消'),
      onOk: async () => {
        setDistributing(true);
        try {
          const res = await api.post('/gas/distribute');
          const { source, distributed } = res.data.data;
          if (distributed.length === 0) {
            message.info(t('所有钱包 gas 均已充足，无需分发'));
          } else {
            message.success(t('已从 {source} 给 {n} 个钱包各分发 gas', { source, n: distributed.length }));
          }
          onRefresh();
        } catch (err) {
          message.error(t('分发失败: ') + ts(err.response?.data?.error || err.message));
        } finally {
          setDistributing(false);
        }
      }
    });
  };

  // 打开转账弹窗
  const openSend = (w) => {
    setSendFrom(w);
    setSendToken('USDT');
    setSendTo(undefined);
    setSendAmount(null);
  };

  const sendTokenBalance = sendFrom
    ? parseFloat(sendToken === 'USDT' ? sendFrom.usdtBalance : sendFrom.usgnaaBalance ?? 0)
    : 0;

  const handleSend = async () => {
    if (!sendFrom || !sendTo || !sendAmount) return;
    setSending(true);
    try {
      const res = await api.post(`/wallets/${sendFrom.address}/send`, {
        token: sendToken,
        to: sendTo,
        amount: sendAmount
      });
      const { txHash } = res.data.data;
      message.success(t('转账成功：{amount} {token}（{tx}...）', { amount: sendAmount, token: sendToken, tx: txHash.slice(0, 12) }));
      setSendFrom(null);
      onRefresh();
    } catch (err) {
      message.error(t('转账失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  // 收款方选项：其他 9 个钱包 + 地址簿条目（与后端白名单一致）
  const recipientOptions = sendFrom
    ? [
        {
          label: t('我的钱包'),
          options: wallets
            .filter((w) => w.address !== sendFrom.address)
            .map((w) => ({ value: w.address, label: `${w.label}  ${w.address.slice(0, 10)}...${w.address.slice(-6)}` }))
        },
        ...(addressBook.length
          ? [
              {
                label: t('地址簿'),
                options: addressBook.map((e) => ({
                  value: e.address,
                  label: `${e.name}  ${e.address.slice(0, 10)}...${e.address.slice(-6)}`
                }))
              }
            ]
          : [])
      ]
    : [];

  const columns = [
    {
      title: t('名称'),
      dataIndex: 'label',
      width: 110,
      // 颜色区分私钥导入状态：蓝色=已导入，灰色=未导入
      render: (label, w) =>
        w.imported ? (
          <Tag color="blue">{label}</Tag>
        ) : (
          <Tooltip title={t('私钥未导入')}>
            <Tag>{label}</Tag>
          </Tooltip>
        )
    },
    {
      title: t('标签'),
      dataIndex: 'tags',
      width: 200,
      render: (tags, w) =>
        tags?.length ? (
          <Space size={[4, 4]} wrap style={{ cursor: 'pointer' }} onClick={() => openTags(w)}>
            {tags.map((t) => (
              <Tag key={t} color={colorOfTag(t)} style={{ marginInlineEnd: 0 }}>
                {t}
              </Tag>
            ))}
          </Space>
        ) : (
          <Button type="link" size="small" icon={<TagsOutlined />} onClick={() => openTags(w)}>
            {t('添加标签')}
          </Button>
        )
    },
    {
      title: t('地址'),
      dataIndex: 'address',
      render: (address) => (
        <Typography.Text
          copyable={{ text: address }}
          style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
        >
          <a href={`https://bscscan.com/address/${address}`} target="_blank" rel="noreferrer">
            {address.slice(0, 10)}...{address.slice(-8)}
          </a>
        </Typography.Text>
      )
    },
    {
      title: t('USDT 余额'),
      dataIndex: 'usdtBalance',
      align: 'right',
      width: 140,
      render: (v) => Number(v).toFixed(2)
    },
    {
      title: t('USGNAA 余额'),
      dataIndex: 'usgnaaBalance',
      align: 'right',
      width: 140,
      render: (v) => (v === undefined ? '-' : Number(v).toFixed(2))
    },
    {
      title: t('BNB 余额（gas）'),
      dataIndex: 'bnbBalance',
      align: 'right',
      width: 200,
      // 有 BNB 时显示链上完整精度值，为零显示 '-'
      render: (v) =>
        !v || parseFloat(v) === 0 ? (
          '-'
        ) : (
          <Typography.Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Typography.Text>
        )
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 220,
      render: (_, w) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => onViewTransactions(w.address)}>
            {t('查看交易')}
          </Button>
          <Button type="link" size="small" icon={<TagsOutlined />} onClick={() => openTags(w)}>
            {t('标签')}
          </Button>
          <Tooltip title={!w.imported ? t('私钥未导入') : parseFloat(w.bnbBalance) < MIN_BNB_FOR_TOPUP ? t('BNB 不足以支付 gas') : ''}>
            <Button
              type="link"
              size="small"
              icon={<SendOutlined />}
              disabled={!w.imported || parseFloat(w.bnbBalance) < MIN_BNB_FOR_TOPUP}
              onClick={() => openSend(w)}
            >
              {t('转账')}
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={t('钱包（{n}）', { n: wallets.length })}
      extra={
        <Space>
          <Button icon={<ThunderboltOutlined />} onClick={handleDistribute} loading={distributing}>
            {t('分发 gas')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
            {t('刷新余额')}
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="address"
        columns={columns}
        dataSource={wallets}
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
      />

      {/* 标签编辑弹窗：下拉选已有标签，也可直接输入新标签后回车 */}
      <Modal
        title={tagWallet ? t('标签 · {label}', { label: tagWallet.label }) : t('标签')}
        open={!!tagWallet}
        onOk={handleSaveTags}
        onCancel={() => setTagWallet(null)}
        okText={t('保存')}
        cancelText={t('取消')}
        confirmLoading={savingTags}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder={t('选择已有标签，或直接输入新标签后回车')}
            value={tagValues}
            onChange={setTagValues}
            options={tagOptions}
            optionFilterProp="value"
            maxTagCount={20}
            tokenSeparators={[',', '，', ' ']}
            allowClear
          />
          <Alert
            type="info"
            showIcon
            message={t('标签用于给钱包分组标记（如：主力、测试、备用）；单个标签最长 32 字符，每个钱包最多 20 个')}
          />
        </Space>
      </Modal>

      {/* 转账弹窗 */}
      <Modal
        title={sendFrom ? t('转账 · {label}', { label: sendFrom.label }) : t('转账')}
        open={!!sendFrom}
        onOk={handleSend}
        onCancel={() => setSendFrom(null)}
        okText={t('确认转账')}
        cancelText={t('取消')}
        confirmLoading={sending}
        okButtonProps={{ disabled: !sendTo || !sendAmount || sendAmount <= 0 || sendAmount > sendTokenBalance }}
        maskClosable={false}
      >
        {sendFrom && (
          <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
            <div>
              <Typography.Text type="secondary">{t('币种')}</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Segmented
                  value={sendToken}
                  onChange={(v) => {
                    setSendToken(v);
                    setSendAmount(null);
                  }}
                  options={['USDT', 'USGNAA']}
                />
                <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
                  {t('可用余额：')}{sendTokenBalance.toFixed(6)} {sendToken}
                </Typography.Text>
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">{t('收款方（限钱包列表与地址簿，防误转）')}</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder={t('选择收款地址')}
                value={sendTo}
                onChange={setSendTo}
                options={recipientOptions}
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div>
              <Typography.Text type="secondary">
                {t('金额')}{' '}
                <Typography.Link onClick={() => setSendAmount(sendTokenBalance)}>{t('全部_max')}</Typography.Link>
              </Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0}
                max={sendTokenBalance}
                precision={6}
                value={sendAmount}
                onChange={setSendAmount}
                placeholder={t('最多 {max}', { max: sendTokenBalance.toFixed(6) })}
              />
            </div>
            <Alert
              type="info"
              showIcon
              message={t('转账将直接上链执行；如需转给列表外的地址，请先在设置 → 地址簿中添加')}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default WalletList;
